import Stack from "@mui/material/Stack";
import Step from "@mui/material/Step";
import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import { styled } from "@mui/material/styles";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import * as yup from "yup";
import { getStateAction } from "../../Actions/SettingAction";
import {
  createTenantAction,
  editTenantAction,
  getCountriesDataAction,
  getTenantCurrenciesAction,
  getTenantDetailByIdAction,
} from "../../Actions/TenantActions";
import {
  MyGroupAddIcon,
  MyPaymentIcon,
  MyVisibilityIcon,
} from "../../Assets/svg/MyIcons";
import BreadcrumbsComponent from "../../components/Common/Breadcrumbs";
import CustomSnackbar from "../Common/snackBar";
import TenantCreationPreview from "./TenantCreationPreview";
import TenantInformationStep from "./TenantInformationStep";
import TenantPlanStructureStep from "./TenantPlanStructureStep";
import { FRONTEND_MESSAGES } from "../../utils/messages";

const { VALIDATION } = FRONTEND_MESSAGES;

// Styled Components for Stepper
const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage:
        "linear-gradient( 95deg,#22419e 0%,#22419e 50%,#22419e 100%)",
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage:
        "linear-gradient( 95deg,#22419e 0%,#22419e 50%,#22419e 100%)",
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor: "#eaeaf0",
    borderRadius: 1,
    ...theme.applyStyles("dark", {
      backgroundColor: theme.palette.grey[800],
    }),
  },
}));

const ColorlibStepIconRoot = styled("div")(({ theme }) => ({
  backgroundColor: "#ccc",
  zIndex: 1,
  color: "#fff",
  width: 50,
  height: 50,
  display: "flex",
  borderRadius: "50%",
  justifyContent: "center",
  alignItems: "center",
  ...theme.applyStyles("dark", {
    backgroundColor: theme.palette.grey[700],
  }),
  variants: [
    {
      props: ({ ownerState }) => ownerState.active,
      style: {
        backgroundImage:
          "linear-gradient( 136deg, #22419e 0%, #22419e 50%,#22419e 100%)",
        boxShadow: "0 4px 10px 0 rgba(0,0,0,.25)",
      },
    },
    {
      props: ({ ownerState }) => ownerState.completed,
      style: {
        backgroundImage:
          "linear-gradient( 136deg, #22419e 0%, #22419e 50%,#22419e 100%)",
      },
    },
  ],
}));

function ColorlibStepIcon(props) {
  const { active, completed, className } = props;

  const icons = {
    1: <MyGroupAddIcon className="text-white" />,
    2: <MyPaymentIcon className="text-white" />,
    3: <MyVisibilityIcon className="text-white" />,
  };

  return (
    <ColorlibStepIconRoot
      ownerState={{ completed, active }}
      className={className}
    >
      {icons[String(props.icon)]}
    </ColorlibStepIconRoot>
  );
}

ColorlibStepIcon.propTypes = {
  active: PropTypes.bool,
  className: PropTypes.string,
  completed: PropTypes.bool,
  icon: PropTypes.node,
};

// Validation Schemas
const merchantInformationObject = {
  firstName: yup
    .string()
    .trim()
    .required(VALIDATION.REQUIRED_FIELD("First name"))
    .min(1, VALIDATION.MIN_LENGTH("First name", 1))
    .max(50, VALIDATION.MAX_LENGTH("First name", 50))
    .matches(/^[a-zA-Z_\-'\u2018\u2019\u2018\u0060\u2013\u2014]+$/, VALIDATION.INVALID_FORMAT("First name")),
  lastName: yup
    .string()
    .trim()
    .required(VALIDATION.REQUIRED_FIELD("Last name"))
    .min(1, VALIDATION.MIN_LENGTH("Last name", 1))
    .max(50, VALIDATION.MAX_LENGTH("Last name", 50))
    .matches(/^[a-zA-Z_\-'\u2018\u2019\u2018\u0060\u2013\u2014]+$/, VALIDATION.INVALID_FORMAT("Last name")),
  companyName: yup
    .string()
    .trim()
    .required(VALIDATION.REQUIRED_FIELD("Company name"))
    .min(1, VALIDATION.MIN_LENGTH("Company name", 1))
    .max(160, VALIDATION.MAX_LENGTH("Company name", 160))
    .matches(/^[a-zA-Z0-9_\-'&\s\u2018\u2019\u2018\u0060\u2013\u2014]+$/, VALIDATION.INVALID_FORMAT("Company name")),
  currency: yup.string().trim().required(VALIDATION.SELECT_OPTION("Currency")),
  email: yup
    .string()
    .trim()
    .required(VALIDATION.REQUIRED_FIELD("Email"))
    .email(VALIDATION.INVALID_EMAIL)
    .min(1, VALIDATION.MIN_LENGTH("Email", 1))
    .max(70, VALIDATION.MAX_LENGTH("Email", 70))
    .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, VALIDATION.INVALID_EMAIL),
  companyEmail: yup
    .string()
    .trim()
    .required(VALIDATION.REQUIRED_FIELD("Company email"))
    .email(VALIDATION.INVALID_EMAIL)
    .min(1, VALIDATION.MIN_LENGTH("Company email", 1))
    .max(70, VALIDATION.MAX_LENGTH("Company email", 70))
    .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, VALIDATION.INVALID_EMAIL),
  companyAddress: yup
    .string()
    .trim()
    .required(VALIDATION.REQUIRED_FIELD("Company address"))
    .min(1, VALIDATION.MIN_LENGTH("Company address", 1))
    .max(250, VALIDATION.MAX_LENGTH("Company address", 250))
    .matches(/^[a-zA-Z0-9\s.,'\-#@%&/()\[\]:\u2018\u2019\u2013\u2014]+$/, VALIDATION.INVALID_FORMAT("Company Address")),
  companyZipCode: yup
    .string()
    .trim()
    .required(VALIDATION.REQUIRED_FIELD("Company zip code"))
    .min(4, VALIDATION.MIN_LENGTH("Company zip code", 4))
    .max(12, VALIDATION.MAX_LENGTH("Company zip code", 12))
    .matches(/^[a-zA-Z0-9\-\u2013\u2014]+$/, VALIDATION.INVALID_FORMAT("Company zip code"))
    .test("not-all-zeros", VALIDATION.INVALID_FORMAT("Company zip code"), (value) => {
      if (!value) return true;
      return !/^0+$/.test(value.replace(/[\-\u2013\u2014]/g, ''));
    }),
  companyCountry: yup.string().required(VALIDATION.SELECT_OPTION("Company country")),
  companyState: yup.string().required(VALIDATION.SELECT_OPTION("Company state")),
  companyPhoneNumber: yup
    .string()
    .required(VALIDATION.REQUIRED_FIELD("Company phone number"))
    .min(5, VALIDATION.MIN_LENGTH("Company phone number", 5))
    .max(15, VALIDATION.MAX_LENGTH("Company phone number", 15))
    .test("valid-phone", VALIDATION.INVALID_PHONE, (value) => {
      if (!value) return false;
      const digits = value.replace(/\D/g, "");
      if (/^0+$/.test(digits)) return false;     
      if (digits.startsWith("0")) return false;     
      return true;
    }),
  brandingName: yup
    .string()
    .max(10, VALIDATION.MAX_LENGTH("Branding Name",10)),
  signatureTitle: yup
    .string()
    .max(100, VALIDATION.MAX_LENGTH("Signature Title", 100)),
  signatureBody: yup.string(),
  domainUrl: yup.string().required(VALIDATION.REQUIRED_FIELD("Domain URL")),
  isVerified: yup
    .boolean()
    .oneOf([true], VALIDATION.INVALID_FORMAT("Domain verification"))
    .required(VALIDATION.REQUIRED_FIELD("Domain verification")),
  whitelistIps: yup.array().of(yup.string()),
};

const validationPlanStructureSchema = yup.object({
  planId: yup.mixed().required(VALIDATION.REQUIRED_FIELD("Plan")),
  billingCycle: yup
    .string()
    .oneOf(["monthly", "yearly"], VALIDATION.INVALID_FORMAT("Billing cycle"))
    .required(VALIDATION.REQUIRED_FIELD("Billing cycle")),
  paymentMode: yup
    .string()
    .oneOf(["online", "offline"], VALIDATION.INVALID_FORMAT("Payment mode"))
    .required(VALIDATION.REQUIRED_FIELD("Payment mode")),
  isAutoPay: yup.string(),
	hideBillingDetails: yup.boolean(),
  hideAiProviders: yup.boolean(),
});

const validationMerchantInformationSchema = yup.object(
  merchantInformationObject
);

const AddEditTenant = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [countryData, setCountryData] = useState([]);
  const [stateData, setStateData] = useState([]);
  const [tenantCurrencies, setTenantCurrencies] = useState([]);
  const [planMapPrice, setPlanMapPrice] = useState({});
  const [merchantDetails, setMerchantDetails] = useState({});
  const [logoRemoved, setLogoRemoved] = useState(false);


  const steps = ["Tenant Information", "Plan Structure", "Preview"];

  const { id = null } = location.state || {};

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCancel = () => {
    navigate("/tenant");
  };

  // Formik for Tenant Information
  const tenantInformationFormik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      companyName: "",
      isVerified: false,
      domainUrl: "",
      currency: "",
      companyEmail: "",
      companyAddress: "",
      companyZipCode: "",
      companyCountry: "",
      companyState: "",
      companyPhoneNumber: "",
      isWhiteListIp: false,
      whitelistIps: [],
      brandingName: "",
      brandingLogo: "",
      signatureTitle: "",
      signatureBody: "",
    },
    validationSchema: validationMerchantInformationSchema,
    validateOnChange: true,
    validateOnBlur: true,
  });

  // Formik for Plan Structure
  const merchantPlanStructureFormik = useFormik({
    initialValues: {
      planId: null,
      paymentMode: "online",
      billingCycle: null,
      isAutoPay: true,
      isCustomPlan: true,
      customPlanDetails: {},
      allowedFeatures: {},
      hideBillingDetails: false,
      hideAiProviders: false,
    },
    validationSchema: validationPlanStructureSchema,
    validateOnChange: true,
    validateOnBlur: true,
  });

  useEffect(() => {
    const fetchMerchantDetails = async () => {
      try {
        const { status, data } = await getTenantDetailByIdAction(id);
        if (status === 200) {
          setMerchantDetails(data);
          let parsedWhitelistIps = [];
          if (data.whitelistIps) {
            try {
              parsedWhitelistIps = typeof data.whitelistIps === "string" ? JSON.parse(data.whitelistIps) : data.whitelistIps;
            } catch (e) {
              parsedWhitelistIps = [];
            }
          }
          const tenantInfoData = {};
          Object.keys(tenantInformationFormik.initialValues).forEach((key) => {
            if (data[key] !== undefined) tenantInfoData[key] = data[key];
          });

          tenantInformationFormik.setValues({
            ...tenantInformationFormik.initialValues,
            ...tenantInfoData,
            signatureTitle: data.companySignature?.title || "",
            signatureBody: data.companySignature?.body || "",
            isVerified: true,
            isWhiteListIp: false,  // When Edit The Tenant We Don't Allow To Edit The Ip Address So Set IsWhiteListIp False
            whitelistIps: parsedWhitelistIps,
          });

          const planStructureData = {};
          Object.keys(merchantPlanStructureFormik.initialValues).forEach((key) => {
            if (data[key] !== undefined) planStructureData[key] = data[key];
          });

          merchantPlanStructureFormik.setValues({
            ...merchantPlanStructureFormik.initialValues,
            ...planStructureData,
          });
        }
      } catch (error) {
        setSnackbar({
          open: true,
          message: error.response.data.message,
          severity: "error",
        });
      }
    };

    if (id) fetchMerchantDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    getMerchantCurrenciesData();
    getCountries();
  }, []);
  useEffect(() => {
    getStateData(tenantInformationFormik?.values?.companyCountry);
  }, [tenantInformationFormik?.values?.companyCountry]);

  // const isStepValid = () => {
  //   if (activeStep === 0) {
  //     // For step 0, check if form is valid AND isVerified is true
  //     const hasErrors =
  //       Object.keys(tenantInformationFormik.errors).length > 0;
  //     const isVerified = tenantInformationFormik.values.isVerified === true;
  //     const isWhiteListIp = tenantInformationFormik.values.isWhiteListIp
  //       ? (tenantInformationFormik.values.whitelistIps?.length || 0) > 0
  //       : true;
  //     return (
  //       !hasErrors &&
  //       isVerified &&
  //       isWhiteListIp &&
  //       tenantInformationFormik.dirty
  //     );
  //   }
  //   if (activeStep === 1) {
  //     const hasErrors =
  //       Object.keys(merchantPlanStructureFormik.errors).length > 0;
  //     // Check if required fields are filled instead of relying on dirty flag
  //     const hasRequiredFields =
  //       merchantPlanStructureFormik.values.planId !== null &&
  //       merchantPlanStructureFormik.values.billingCycle !== null &&
  //       merchantPlanStructureFormik.values.paymentMode !== null;

  //     // Check if at least one allowed feature is enabled (user_chat or support_tickets)
  //     const allowedFeatures = merchantPlanStructureFormik.values.allowedFeatures || {};
  //     const hasAtLeastOneFeature = allowedFeatures.user_chat === true || allowedFeatures.support_tickets === true;

  //     return !hasErrors && hasRequiredFields && hasAtLeastOneFeature;
  //   }
  //   // For step 2 (Preview/Finish), validate that all previous steps are valid
  //   if (activeStep === 2) {
  //     // Validate step 0 - don't require dirty flag since user has already navigated through steps
  //     const step0HasErrors =
  //       Object.keys(tenantInformationFormik.errors).length > 0;
  //     const step0IsVerified =
  //       tenantInformationFormik.values.isVerified === true;
  //     const step0IsWhiteListIp = tenantInformationFormik.values
  //       .isWhiteListIp
  //       ? (tenantInformationFormik.values.whitelistIps?.length || 0) >= 1
  //       : true;
  //     const step0Valid =
  //       !step0HasErrors && step0IsVerified && step0IsWhiteListIp;

  //     // Validate step 1
  //     const step1HasErrors =
  //       Object.keys(merchantPlanStructureFormik.errors).length > 0;
  //     const step1HasRequiredFields =
  //       merchantPlanStructureFormik.values.planId !== null &&
  //       merchantPlanStructureFormik.values.billingCycle !== null &&
  //       merchantPlanStructureFormik.values.paymentMode !== null;

  //     // Check if at least one allowed feature is enabled (user_chat or support_tickets)
  //     const allowedFeatures = merchantPlanStructureFormik.values.allowedFeatures || {};
  //     const step1HasAtLeastOneFeature = allowedFeatures.user_chat === true || allowedFeatures.support_tickets === true;

  //     const step1Valid = !step1HasErrors && step1HasRequiredFields && step1HasAtLeastOneFeature;

  //     return step0Valid && step1Valid;
  //   }
  //   return true;
  // };

  const handleNext = async () => {
    if (activeStep === 0) {
      const errors = await tenantInformationFormik.validateForm();
      tenantInformationFormik.setTouched(
        Object.keys(tenantInformationFormik.initialValues).reduce(
          (acc, key) => ({ ...acc, [key]: true }),
          {}
        )
      );
      // Check if isVerified is true
      if (!tenantInformationFormik.values.isVerified) {
        tenantInformationFormik.setFieldError(
          "isVerified",
          "Please verify the domain before proceeding."
        );
        return;
      }
      if ( !isEditing &&
        tenantInformationFormik.values.isWhiteListIp &&
        tenantInformationFormik?.values?.whitelistIps?.length === 0
      ) {
        tenantInformationFormik.setFieldError(
          "whitelistIps",
          "Please add Atleast One Ip Address."
        );
        return;
      }

      if (Object.keys(errors).length === 0) {
        setActiveStep((prev) => prev + 1);
      }
    } else if (activeStep === 1) {
      const errors = await merchantPlanStructureFormik.validateForm();
      merchantPlanStructureFormik.setTouched(
        Object.keys(merchantPlanStructureFormik.initialValues).reduce(
          (acc, key) => ({ ...acc, [key]: true }),
          {}
        )
      );

      // Check if at least one allowed feature is enabled
      const allowedFeatures = merchantPlanStructureFormik.values.allowedFeatures || {};
      const hasAtLeastOneFeature =
        allowedFeatures.user_chat === true ||
        allowedFeatures.support_tickets === true ||
        allowedFeatures.ticket_v2 === true;

      if (!hasAtLeastOneFeature) {
        // Set error to show validation message
        const errorMessage =
          "Please enable at least one feature (User Chat, Ticket v1, or Ticket v2) before proceeding.";
        merchantPlanStructureFormik.setFieldError("allowedFeatures", errorMessage);
        merchantPlanStructureFormik.setFieldTouched("allowedFeatures", true, false);
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: "error",
        });
        return;
      } else {
        // Clear error if feature is selected
        merchantPlanStructureFormik.setFieldError("allowedFeatures", undefined);
      }

      if (Object.keys(errors).length === 0) {
        setActiveStep((prev) => prev + 1);
      }
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleEditing = (edit) => {
    setIsEditing(edit);
  };

  const handleFinish = async () => {
    const isPlanChanged =
      id &&
      merchantDetails?.planId &&
      String(merchantDetails.planId) !==
        String(merchantPlanStructureFormik.values.planId);
    const hasActiveDowngradeRequest = Boolean(
      merchantPlanStructureFormik.values.isDowngradeRequested ||
        merchantDetails?.isDowngradeRequested,
    );

    if (id && hasActiveDowngradeRequest && isPlanChanged) {
      const confirmResult = await Swal.fire({
        title: "Confirm Plan Update?",
        html: `
          <p class="mb-3">Are you sure you want to finish and update this tenant?</p>
          <div class="alert alert-warning bg-warning-subtle border border-warning-subtle text-start p-3 mb-0">
            <strong>Note:</strong> All previous active downgrade requests will be automatically cancelled.
          </div>
        `,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#22419e",
        cancelButtonColor: "#777777",
        confirmButtonText: "Yes, Finish",
        cancelButtonText: "Cancel",
      });

      if (!confirmResult.isConfirmed) {
        return;
      }
    }

    setLoading(true);

    // Check if brandingLogo is a File object
    const hasBrandingFile = tenantInformationFormik.values.brandingLogo instanceof File;
    
    const formData = new FormData();
    const ipRestriction = tenantInformationFormik.values.isWhiteListIp ? true : false;
    
    // Tenant Information
    formData.append("firstName", tenantInformationFormik.values.firstName || "");
    formData.append("lastName", tenantInformationFormik.values.lastName || "");
    formData.append("email", tenantInformationFormik.values.email || "");
    formData.append("companyName", tenantInformationFormik.values.companyName || "");
    formData.append("isVerified", tenantInformationFormik.values.isVerified);
    formData.append("domainUrl", tenantInformationFormik.values.domainUrl || "");
    formData.append("currency", tenantInformationFormik.values.currency || "");
    formData.append("companyEmail", tenantInformationFormik.values.companyEmail || "");
    formData.append("companyAddress", tenantInformationFormik.values.companyAddress || "");
    formData.append("companyZipCode", tenantInformationFormik.values.companyZipCode || "");
    formData.append("companyCountry", tenantInformationFormik.values.companyCountry || "");
    formData.append("companyState", tenantInformationFormik.values.companyState || "");
    formData.append("companyPhoneNumber", tenantInformationFormik.values.companyPhoneNumber || "");
    formData.append("brandingName", tenantInformationFormik.values.brandingName || "");
    formData.append("ipRestriction", ipRestriction);
    formData.append("whitelistIps", ipRestriction? JSON.stringify(tenantInformationFormik.values.whitelistIps || []): JSON.stringify([]));
    const companySignature = {
      title: tenantInformationFormik.values.signatureTitle || "",
      body: tenantInformationFormik.values.signatureBody || ""
    };
    formData.append("companySignature", JSON.stringify(companySignature));
    
    // Tenant Plan Structure
    formData.append("planId", merchantPlanStructureFormik.values.planId || "");
    formData.append("paymentMode", merchantPlanStructureFormik.values.paymentMode || "");
    formData.append("billingCycle", merchantPlanStructureFormik.values.billingCycle || "");
    formData.append("isAutoPay", merchantPlanStructureFormik.values.paymentMode == "offline" ? false : merchantPlanStructureFormik.values.isAutoPay || true);
    formData.append("isCustomPlan", Boolean(merchantPlanStructureFormik.values.isCustomPlan) || false);
    formData.append("customPlanDetails", JSON.stringify(
      merchantPlanStructureFormik.values.customPlanDetails || {}
    ));
    formData.append("allowedFeatures", JSON.stringify(
      merchantPlanStructureFormik.values.allowedFeatures || {}
    ));
    formData.append("hideBillingDetails", merchantPlanStructureFormik.values.hideBillingDetails || false);
    formData.append("hideAiProviders", merchantPlanStructureFormik.values.hideAiProviders || false);
    
    // Determine isImageEdited flag
    let isImageEdited = "false";
    if (hasBrandingFile) {
      isImageEdited = "true";
    } else if (logoRemoved) {
      isImageEdited = "false";
    }
    formData.append("isImageEdited", isImageEdited);
    
    // Add logo removal flag if logo was removed
    if (logoRemoved) {
      formData.append("logoRemoved", "true");
    }
    
    // Add the file if it exists
    if (hasBrandingFile) {
      formData.append("brandingLogo", tenantInformationFormik.values.brandingLogo);
    }

    try {
      const res = id
        ? await editTenantAction(formData, id)
        : await createTenantAction(formData);

      const { message, status } = res;
      if (status === 200) {
        setSnackbar({
          open: true,
          message: message || "Tenant data has been created successfully",
          severity: "success",
        });
        setTimeout(() => {
          navigate("/tenant");
          tenantInformationFormik.resetForm();
          merchantPlanStructureFormik.resetForm();
        }, 500);
      } else {
        setSnackbar({
          open: true,
          message: message || "Something went wrong while creating tenant",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.message || error?.response?.data?.message,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <TenantInformationStep
            tenantId={id}
            formik={tenantInformationFormik}
            handleEditing={handleEditing}
            isEditing={isEditing}
            tenantCurrencies={tenantCurrencies}
            countryData={countryData}
            stateData={stateData}
            merchantDetails={merchantDetails}
            logoRemoved={logoRemoved}
            setLogoRemoved={setLogoRemoved}
          />
        );
      case 1:
        return (
          <TenantPlanStructureStep
            tenantId={id}
            formik={merchantPlanStructureFormik}
            planMapPrice={planMapPrice}
            setPlanMapPrice={setPlanMapPrice}
            selectedCurrency={tenantInformationFormik.values.currency}
            tenantCurrencies={tenantCurrencies}
            merchantDetails={merchantDetails}
          />
        );
      case 2:
        // Calculate if plan has changed from database value
        const isPlanChanged = id && merchantDetails?.planId &&
          String(merchantDetails.planId) !== String(merchantPlanStructureFormik.values.planId);

        return (
          <TenantCreationPreview
            previewData={{
              ...tenantInformationFormik.values,
              ...merchantPlanStructureFormik.values,
            }}
            planMapPrice={planMapPrice}
            selectedCurrency={tenantInformationFormik.values.currency}
            tenantCurrencies={tenantCurrencies}
            merchantDetails={merchantDetails}
            isPlanChanged={isPlanChanged}
          />
        );
      default:
        return null;
    }
  };

  const getCountries = async () => {
    try {
      const res = await getCountriesDataAction();
      const { data } = res?.data;
      setCountryData(data);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to fetch countries",
        severity: "error",
      });
    }
  };

  const getStateData = async (name) => {
    if (!name) {
      setStateData([]);
      return;
    }
    try {
      const res = await getStateAction({ countryName: name });
      const { data } = res?.data;
      setStateData(data);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.message || "Failed to fetch states",
        severity: "error",
      });
    }
  };

  const getMerchantCurrenciesData = async () => {
    try {
      const res = await getTenantCurrenciesAction();
      setTenantCurrencies(res?.data?.data || []);
    } catch (error) {
      console.error("err=>", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to fetch currencies",
        severity: "error",
      });
    }
  };

  return (
    <div id="kt_content_container">
      <BreadcrumbsComponent />

      <div className="container-fluid p-4">
        <div className="row">
          <div className="col-xl-9 mx-auto">
            <div className="row px-5">
              <Stack sx={{ width: "100%" }} my={4} spacing={5}>
                <Stepper
                  alternativeLabel
                  activeStep={activeStep}
                  connector={<ColorlibConnector />}
                >
                  {steps.map((label, index) => (
                    <Step key={label} completed={activeStep > index}>
                      <StepLabel StepIconComponent={ColorlibStepIcon}>
                        {label}
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Stack>

              <div className="px-5">{getStepContent(activeStep)}</div>

              <div className="d-flex justify-content-between align-items-center mt-5 px-5">
                {activeStep === 0 ? (
                  <button
                    className="btn btn-sm btn-outline btn-outline-primary"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                ) : (
                  <button
                    className="btn btn-sm btn-outline btn-outline-primary"
                    onClick={handleBack}
                    disabled={loading}
                  >
                    Back
                  </button>
                )}
                <button
                  className={`btn btn-primary btn-sm ${activeStep === 0 ? "ms-auto" : ""
                    }`}
                  onClick={
                    activeStep === steps.length - 1 ? handleFinish : handleNext
                  }
                  // disabled={loading || !isStepValid()}
                  disabled={loading}
                >
                  {activeStep === steps.length - 1 ? "Finish" : "Next"}
                  {loading && (
                    <span
                      className="spinner-border spinner-border-sm ms-2"
                      role="status"
                      aria-hidden="true"
                    />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleCloseSnackbar}
      />
    </div>
  );
};

export default AddEditTenant;
